import {useCallback, useMemo} from 'react';

import ProductModel from 'coreClasses/product/ProductModel';
import VariantModel from 'coreClasses/product/VariantModel';

import {useGeneralProvider} from 'brandComponents/_app-config/GeneralProvider';
import {useLocalesMgr} from 'coreHooks';
import {useCategory} from 'coreHooks/categories';

import {
	ICategoryMatching,
	IConvertedSizes,
	type IGeolocalizedSizeOptionsProp,
	IGeolocalizedSizesJSON,
	ISize,
	ISizingCategory
} from './IGeolocalizedSize';

/**
 * Hook to retrieve the product size(s) based on the current country and the geolocalized sizing configuration.
 *
 * @param {ProductModel | VariantModel} product - the product where to extract the size(s) from.
 * @param {Record<string, any>} options
 * @param {boolean} options.getBySize - true if it has to search the converted size value by the one in the options object
 * @param {string} options.EUSize - the EU size to convert
 * @param {string} options.productID - the id of the product (used as key for the returned object)
 * @returns {IConvertedSizes} converted size(s).
 */
const useGeolocalizedSizes = (product: ProductModel | VariantModel, options?: Partial<IGeolocalizedSizeOptionsProp>): IConvertedSizes => {
	const {managerRepository} = useGeneralProvider();
	const configurationsMgr = managerRepository.getConfigurationsMgr();
	const localesMgr = useLocalesMgr();
	const {getByCategory, getBySize, EUSize, cgid, productID, countryCode} = options || {};
	const currentCountryCode: string = countryCode || localesMgr.getCurrentCountryCode(true);
	const productCategoryId: string = product?.getCategoryId();
	const productCat = useCategory(productCategoryId);
	const catForCgid = useCategory(cgid);
	const sizeCountries = [];
	let geolocalizedSizesJSON: IGeolocalizedSizesJSON = configurationsMgr.get('sitePreferences.sizingGeoJSON');

	const getSizingKey = useCallback(
		(categoryTree: Array<string>): string => {
			const applyByDefault: boolean = geolocalizedSizesJSON?.applyByDefault;
			const categoryMatchObj: ICategoryMatching = geolocalizedSizesJSON?.categoryMatching || {};
			let sizingKey: string;

			if (!categoryTree && applyByDefault) {
				sizingKey = 'default';
			} else if (categoryMatchObj && categoryTree) {
				for (let i = categoryTree.length - 1; i >= 0; i--) {
					const cgid = categoryTree[i];

					sizingKey = Object.entries(categoryMatchObj)?.find(([, cgids]) => cgids.includes(cgid))?.[0];
				}

				sizingKey = 'default';
			}

			return sizingKey;
		},
		[product, geolocalizedSizesJSON],
	);

	const getConvertedSize = useCallback(
		(size: string): string => {
			const allSizingTypes: ISizingCategory = geolocalizedSizesJSON?.sizingGeo || {default: []};
			let sizingType: ISize[] = allSizingTypes[getSizingKey(productCat?.getParentCategoryTreeIds())];
			let matchingSize: string = size;

			if (sizingType) {
				const matchingSizes: ISize = sizingType.find((item) => item.EU.toString() === size);

				if (matchingSizes?.[currentCountryCode]) {
					matchingSize = matchingSizes[currentCountryCode].toString();
					!sizeCountries.includes(currentCountryCode) && sizeCountries.push(currentCountryCode);
				} else {
					!sizeCountries.includes('EU') && sizeCountries.push('EU');
				}
			}

			return matchingSize;
		},
		[product, currentCountryCode],
	);

	const getConvertedSizeByCategory = useCallback(() => {
		const allSizingTypes: ISizingCategory = geolocalizedSizesJSON?.sizingGeo || {default: []};
		let sizingType: ISize[] = allSizingTypes[getSizingKey(catForCgid?.getParentCategoryTreeIds())];
		let categorySizes: Record<string, string> = {};

		sizingType?.forEach((size) => {
			categorySizes[size.EU] = size[currentCountryCode] || size.EU;
		});

		return categorySizes;
	}, [cgid, currentCountryCode]);

	return useMemo(() => {
		let sizes: Record<string, string> = {};

		if (getByCategory && cgid) {
			sizes = getConvertedSizeByCategory();
		} else if (getBySize && EUSize) {
			const key = productID || EUSize;
			sizes[key] = getConvertedSize(EUSize);
		} else if (product.isVariationGroup()) {
			let variants = product.getVariants();

			if (variants.length) {
				variants.forEach((variant) => {
					sizes[variant.getProductId()] = getConvertedSize(variant.getSize());
				});
			} else {
				product.variants?.forEach((variant) => {
					sizes[variant.productId] = getConvertedSize(variant.variationValues.size);
				});
			}
		} else if (product.isVariant()) {
			sizes[product.getProductId()] = getConvertedSize(product.getSize());
		}

		if (sizeCountries.length === 1) {
			sizes.sizeCountry = sizeCountries[0];
		}

		return sizes;
	}, [product, currentCountryCode]);
};

export default useGeolocalizedSizes;
